import { DragHandle, Lock } from '@mui/icons-material';
import { Switch, Typography } from '@mui/material';
import DrawerMenuItem from 'components/AppDrawer/DrawerMenuItem';
import { isEqual, pick } from 'lodash';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  DragDropContext,
  DragDropContextProps,
  Draggable,
  Droppable,
  DroppableProps,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import { ColumnConfig } from '../contexts';
import { ControlComponentProps, useOrderControl, useVisibilityControl } from '../controls';
import { BaseRow } from '../types';
import { columnToJson } from '../utils';
import useTableControlsStyles from './styles';
import Search from 'components/Search';

type ColumnsListProps = Pick<
  ControlComponentProps,
  'getControl' | 'controlledColumns' | 'showSearch'
>;

const ColumnsList: React.FunctionComponent<ColumnsListProps> = (props) => {
  const { getControl, controlledColumns, showSearch } = props;
  const { t } = useTranslation('common');
  const classes = useTableControlsStyles(props);

  const visibility = useVisibilityControl(getControl);
  const order = useOrderControl(getControl);
  const [searchValue, setSearchValue] = useState<string>();
  const [columnFilteredItem, setColumnFilteredItems] = useState<ColumnConfig[]>([]);

  const onVisibilityChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    checked ? visibility.handlers.show(e.target.name) : visibility.handlers.hide(e.target.name);
  };

  const renderColumnControl = (column: ColumnConfig<BaseRow>, index: number) => {
    const { name, title } = column;

    const isColumnVisible = visibility.handlers.isVisible(name);
    const isSwitchDisabled = !visibility.hasControl(name);
    const canReorder = showSearch && searchValue ? false : order.hasControl(name);

    const toggle = () => {
      isColumnVisible ? visibility.handlers.hide(name) : visibility.handlers.show(name);
    };

    return (
      <Draggable key={name} draggableId={name} index={index} isDragDisabled={!canReorder}>
        {(dragProvided) => (
          <div
            key={name}
            ref={dragProvided.innerRef}
            {...dragProvided.draggableProps}
            {...dragProvided.dragHandleProps}
            style={{
              ...dragProvided.draggableProps.style,
              // using in a drawer breaks without this
              // https://github.com/atlassian/react-beautiful-dnd/issues/485#issuecomment-761608828
              top: 'auto !important',
              left: 'auto !important',
            }}
            aria-label={t('reorder', { column: title })}
          >
            <DrawerMenuItem
              key={`${name}_item`}
              onClick={toggle}
              icon={canReorder ? <DragHandle /> : <Lock />}
              tooltip={t('toggleReorder', { column: title })}
            >
              <Typography className={classes.title}>{title}</Typography>
              <div className={classes.buttonWrapper}>
                <Switch
                  name={name}
                  disabled={isSwitchDisabled}
                  checked={isColumnVisible}
                  onChange={onVisibilityChange}
                />
              </div>
            </DrawerMenuItem>
          </div>
        )}
      </Draggable>
    );
  };

  useEffect(() => {
    if (!searchValue && showSearch) {
      setColumnFilteredItems(controlledColumns);
    }
  }, [searchValue, controlledColumns, showSearch]);

  const handleColumnSearchFilter = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    const filteredArray = controlledColumns.filter((option: any) =>
      option.title.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setColumnFilteredItems(filteredArray);
  };

  const renderColumns: DroppableProps['children'] = (dropProvided, _snapshot) => {
    return (
      <div {...dropProvided.droppableProps} ref={dropProvided.innerRef}>
        <div>
          {showSearch ? (
            <>
              <Search onChange={handleColumnSearchFilter} fullWidth autoComplete='off' />
              <div {...dropProvided.droppableProps} ref={dropProvided.innerRef}>
                {columnFilteredItem.map(renderColumnControl)}
                {!columnFilteredItem.length && (
                  <DrawerMenuItem>
                    <Typography className={classes.noMatches}>{t('noMatches')}</Typography>
                  </DrawerMenuItem>
                )}
              </div>
            </>
          ) : (
            controlledColumns.map(renderColumnControl)
          )}
          {dropProvided.placeholder}
        </div>
        {dropProvided.placeholder}
      </div>
    );
  };

  const onDragEnd: DragDropContextProps['onDragEnd'] = (result, _provided) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    order.handlers.set(result.draggableId, result.destination.index);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId='tableColumns'>{renderColumns}</Droppable>
    </DragDropContext>
  );
};

export default React.memo<ColumnsListProps>(ColumnsList, (prev, next) => {
  const prevColumns = prev.controlledColumns.map(columnToJson);
  const prevProps = pick(prev, 'getControl');

  const nextColumns = next.controlledColumns.map(columnToJson);
  const nextProps = pick(next, 'getControl');

  // we only diff the columns on values we expose
  const sameColumns = isEqual(prevColumns, nextColumns);
  const sameProps = isEqual(prevProps, nextProps);
  return sameColumns && sameProps;
});
